import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Copy, Edit, Trash2, FileSpreadsheet } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { LoadingSpinner } from "../../components/ui/loadingSpinner/loadingSpinner";
import { useWorkPaperTemplates } from "../../hooks/useWorkPaperTemplates";
import {
  FeatureFlag,
  useFeatureFlag,
  useFeatureFlagsReady,
} from "../../contexts/launchDarklyContext";
import {
  WorkPaperTemplate,
  getTemplateTypeLabel,
} from "../../models/workPaperTemplate";

export function WorkPaperTemplatesPage() {
  // Without Work Steps every template is a control template, so the column
  // would only ever say Control. Shown only once flags are authoritative, so
  // the anonymous value can't flash it in or out.
  const flagsReady = useFeatureFlagsReady();
  const workStepsFlag = useFeatureFlag(FeatureFlag.workStepsIntegration);
  const workStepsEnabled = flagsReady && workStepsFlag;
  const navigate = useNavigate();
  const { loading, templates, duplicateTemplate, deleteTemplate } =
    useWorkPaperTemplates();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] =
    useState<WorkPaperTemplate | null>(null);

  const handleCreate = () => {
    navigate("/audit/work-paper-templates/new");
  };

  const handleEdit = (templateId: string) => {
    navigate(`/audit/work-paper-templates/${templateId}/edit`);
  };

  const handleDuplicate = async (templateId: string) => {
    const newTemplateId = await duplicateTemplate(templateId);
    if (newTemplateId) {
      navigate(`/audit/work-paper-templates/${newTemplateId}/edit`);
    }
  };

  const handleDeleteClick = (template: WorkPaperTemplate) => {
    if (template.isBaseTemplate) {
      return; // Should never happen due to UI
    }
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (templateToDelete) {
      await deleteTemplate(templateToDelete.id);
      setTemplateToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col w-full mx-auto overflow-y-auto">
      <div className="px-4 mx-auto flex flex-col w-full max-w-6xl mt-16">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Workpaper Templates</h1>
            <p className="text-muted-foreground mt-1">
              Manage templates for generating workpapers
            </p>
          </div>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Create Template
          </Button>
        </div>

        {/* Templates Table */}
        {templates.length === 0 ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
            <FileSpreadsheet className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No templates yet
            </h3>
            <p className="text-gray-500 mb-4">
              Get started by creating your first workpaper template
            </p>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </div>
        ) : (
          <div className="border-x mb-8">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  {workStepsEnabled && <TableHead>Template Type</TableHead>}
                  <TableHead>Created</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell className="font-medium">
                      {template.name}
                    </TableCell>
                    <TableCell>
                      {template.isBaseTemplate ? (
                        <Badge
                          variant="outline"
                          className="bg-blue-50 text-blue-700 border-blue-300"
                        >
                          Base Template
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-green-50 text-green-700 border-green-300"
                        >
                          Custom
                        </Badge>
                      )}
                    </TableCell>
                    {workStepsEnabled && (
                      <TableCell>
                        {getTemplateTypeLabel(template.type)}
                      </TableCell>
                    )}
                    <TableCell className="text-muted-foreground">
                      {formatDate(template.createdAt)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(template.updatedAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(template.id)}
                          title={
                            template.isBaseTemplate
                              ? "View template"
                              : "Edit template"
                          }
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDuplicate(template.id)}
                          title="Duplicate template"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {!template.isBaseTemplate && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(template)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete template"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This
              action cannot be undone and will delete all fields in this
              template.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
